import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { PlusIcon, ClockIcon, RepeatIcon } from 'lucide-react';
import { MediaView } from '../shared/MediaView';
import { useCartActions, useOverlayActions } from '../../contexts/StoreContext';
import { useCatalog } from '../../contexts/CatalogContext';
import { kuurLabel, treatmentDuration, treatmentPrice } from '../../utils/catalog';
import { ease } from '../../core/format/motion';
import { euro } from '../../core/format/currency';
import { hasMedia } from '../../core/rules/media';
import { isAllSoldOut } from '../../core/rules/stock';
import { kuurProductId } from '../../core/rules/kuur';
import { shopLabels } from '../../data/shopLabels';
import { usePages } from '../../contexts/SiteContext';
import { useFeatures } from '../../hooks/useFeatures';
import { btnPrimaryLg, btnSecondary, btnSecondaryLg } from '../../ui/styles';
import { track } from '../../utils/analytics';
import type { Treatment } from '../../core/types/catalog';

/**
 * Behandelingen als uitklaplijst: één tegelijk open. Uitgeklapt: het beeld (als er een is, uit
 * het beheer) en daarnaast de uitleg, duur en prijs, de boekknop, en het thuisprotocol erbij.
 */
export function TreatmentList({
  items
}: {
  items: Treatment[];
}) {
  const pages = usePages();
  const {
    openBooking
  } = useOverlayActions();
  const {
    addRoutine,
    add
  } = useCartActions();
  const {
    routineTotals,
    routineForTreatment,
    productById
  } = useCatalog();
  const {
    prepaidKuur
  } = useFeatures();
  const kuurProduct = (slug: string) => productById(kuurProductId(slug));
  const buyKuur = (slug: string) => {
    const p = kuurProduct(slug);
    if (p) add(p);
  };
  const [openSlug, setOpenSlug] = useState<string | null>(items[0]?.slug ?? null);
  const reduce = useReducedMotion();
  // Zelfde getal als in "Waar ik voor sta" op de homepage (Paginateksten › Home › Manifest).
  const homeShare = Math.min(100, Math.max(0, Math.round(pages.home.manifest.homeShare)));
  return <ul className="border-t border-ink/15" data-id="element-8460" data-mp="s917rmc8or2q">
      {items.map(t => {
      const expanded = openSlug === t.slug;
      const panelId = `treatment-${t.slug}`;
      const routine = routineForTreatment(t.slug);
      const totals = routine ? routineTotals(routine) : null;
      const allSoldOut = totals ? isAllSoldOut(totals.items) : false;
      const media = hasMedia(t.media) ? t.media : null;
      const kuur = prepaidKuur ? kuurProduct(t.slug) : undefined;
      return <li key={t.slug} className="border-b border-ink/15" data-id="element-8461" data-mp="59d5vm75qfuq">
            <h3 data-id="element-8462" data-mp="5b6db3plh43s">
              <button type="button" aria-expanded={expanded} aria-controls={panelId} onClick={() => {
            setOpenSlug(expanded ? null : t.slug);
            if (!expanded && t.kuur) track('kuur-bekeken', {
              behandeling: t.slug
            });
          }} className="group flex min-h-[64px] w-full items-baseline gap-4 py-6 text-left sm:gap-8 sm:py-7" data-id="element-8463" data-mp="5bg1clr2x16u">
                <span className="min-w-0 flex-1" data-id="element-8464" data-mp="37l0bqi3z8wls">
                  <span className="block font-display text-[1.6rem] font-light leading-tight text-ink transition-colors duration-150 ease-soft group-hover:text-clay sm:text-[2rem]" data-id="element-8465" data-mp="3clemi7wyqkni">
                    {t.name}
                  </span>
                  <span className="mt-1.5 block text-[14px] text-muted" data-id="element-8466" data-mp="33lzz5hc3eq78">{t.tagline}</span>
                  {/* Telefoon: duur onder de naam, zodat de prijs rechts genoeg ruimte heeft. */}
                  <span className="mt-1 block text-[13px] text-muted sm:hidden" data-id="element-8467" data-mp="3vce7yz591cc1">{treatmentDuration(t)}</span>
                </span>
                <span className="hidden whitespace-nowrap text-[14px] text-muted sm:block" data-id="element-8468" data-mp="3hkhfgtzo50f4">{treatmentDuration(t)}</span>
                <span className="whitespace-nowrap font-display text-xl tabular-nums text-ink" data-id="element-8469" data-mp="3mhhlj4wiq368">{treatmentPrice(t)}</span>
                {/* Eén plus die een kwartslag draait tot een kruis: zelfde icoon, geen wissel. */}
                <span aria-hidden="true" className={`flex h-8 w-8 shrink-0 items-center justify-center self-center rounded-full border transition-[transform,border-color,color] duration-200 ease-soft ${expanded ? 'rotate-45 border-ink text-ink' : 'border-ink/15 text-muted group-hover:border-ink/40 group-hover:text-ink'}`} data-id="element-8470" data-mp="2qe2g12p3w0s4">
                  <PlusIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-8471" data-mp="3951yrm60cecy" />
                </span>
              </button>
            </h3>

            <AnimatePresence initial={false} data-id="element-8472" data-mp="353qezx9tenco">
              {expanded && <motion.div id={panelId} initial={reduce ? {
            opacity: 0
          } : {
            height: 0,
            opacity: 0
          }} animate={reduce ? {
            opacity: 1
          } : {
            height: 'auto',
            opacity: 1
          }} exit={reduce ? {
            opacity: 0
          } : {
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.24,
            ease
          }} className="overflow-hidden" data-id="element-8473" data-mp="30bgitzi3wr1y">
                  {/* Twee kolommen (beeld · alles over de behandeling); de boekknop staat direct onder de uitleg, nooit in een smalle zijkolom. */}
                  <div className="grid gap-8 pb-10 md:grid-cols-12 md:gap-10" data-id="element-8474" data-mp="9691lzyirudc">
                    {media && <MediaView media={media} sizes="(min-width: 768px) 30vw, 100vw" className="aspect-[16/10] w-full md:col-span-5 md:aspect-[4/5]" data-id="element-8475" data-mp="d7uo8d0uvn0q" />}

                    <div className={media ? 'md:col-span-7' : 'md:col-span-12 lg:max-w-3xl'} data-id="element-8476" data-mp="n5s9crnf53p4">
                      <p className="text-[16px] leading-relaxed text-muted" data-id="element-8477" data-mp="rwiga9bhtelx">{t.description}</p>
                      <ul className="mt-5 flex flex-wrap gap-2" aria-label={`Waar ${t.name} op werkt`} data-id="element-8478" data-mp="ryux2j5ilixe">
                        {t.focus.map(f => <li key={f} className="rounded-full border border-ink/15 px-3 py-1 text-[13px] text-muted" data-id="element-8479" data-mp="10yhxp2qf6kpe">
                            {f}
                          </li>)}
                        {t.bindweefsel && <li className="rounded-full bg-blush px-3 py-1 text-[13px] text-ink" data-id="element-8480" data-mp="31zo68xreaq89">Incl. bindweefselmassage</li>}
                      </ul>

                      <dl className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[14px] text-muted" data-id="element-8481" data-mp="3bpr69hwxp4g3">
                        <div className="flex items-center gap-2" data-id="element-8482" data-mp="36xzjligwfiv8">
                          <ClockIcon className="h-3.5 w-3.5 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8483" data-mp="36wocb8o8vszd" />
                          <dt className="sr-only" data-id="element-8484" data-mp="3goawhuik92e9">Duur en prijs</dt>
                          <dd data-id="element-8485" data-mp="3go17oa9951a5">
                            {treatmentDuration(t)} · {treatmentPrice(t)}
                          </dd>
                        </div>
                        {t.kuur && <div className="flex items-center gap-2" data-id="element-8486" data-mp="3pmwbjfotpzaa">
                            <RepeatIcon className="h-3.5 w-3.5 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8487" data-mp="20au6bcz7outy" />
                            <dt className="sr-only" data-id="element-8488" data-mp="1vciy16okesdu">Kuur</dt>
                            <dd data-id="element-8489" data-mp="asf7txojog3m">{kuurLabel(t)}</dd>
                          </div>}
                      </dl>

                      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-8490" data-mp="fribpessgga0">
                        <button type="button" onClick={() => openBooking(t.slug)} aria-haspopup="dialog" className={btnPrimaryLg} data-id="element-8491" data-mp="1ztzzcmgbttk">
                          Plan deze behandeling<span className="sr-only" data-id="element-8492" data-mp="3o7tmwlu4vxwv">: {t.name}</span>
                        </button>
                        {kuur && <button type="button" onClick={() => buyKuur(t.slug)} className={btnSecondaryLg} data-id="element-8493" data-mp="3t7y1u7fe8kkz">
                            Kuur vooruit betalen<span className="sr-only" data-id="element-8494" data-mp="3f8yer47j4dqx">: {t.name}</span>
                          </button>}
                      </div>
                      {kuur && <p className="mt-3 max-w-xl text-[13px] leading-relaxed text-muted" data-id="element-8495" data-mp="1dgacax2pluc7">
                          {kuur.note} Stop je eerder, dan krijg je de niet-gebruikte sessies terug (gedane sessies tegen de losse prijs).
                        </p>}

                      {routine && totals && <div className="mt-7 rounded-2xl bg-sand p-5 sm:p-6" data-id="element-8496" data-mp="3rnlfon9syxsk">
                          <p className="text-[14px] text-ink" data-id="element-8497" data-mp="3rlifjaexroa4">
                            {homeShare > 0 ? `Thuis verder: daar haal je ${homeShare}% van je resultaat` : 'Thuis verder'}
                          </p>
                          <div className="mt-3 flex flex-wrap items-center justify-between gap-4" data-id="element-8498" data-mp="3ikkcovg8hmt4">
                            <div className="min-w-0" data-id="element-8499" data-mp="3dq6iuxh7znqk">
                              <p className="font-display text-xl text-ink" data-id="element-8500" data-mp="38pimrxe4wgph">{routine.name}</p>
                              <p className="mt-1 text-[13px] text-muted" data-id="element-8501" data-mp="33qpfimpfxlxr">{totals.items.map(p => p.name).join(' · ')}</p>
                            </div>
                            {allSoldOut ? <span className="text-[13px] text-clay" data-id="element-8502" data-mp="11zks62ujj7j8">{shopLabels.soldOutShort}</span> : <button type="button" onClick={() => addRoutine(routine.id)} className={btnSecondary} data-id="element-8503" data-mp="1ad795n8pzpt8">
                                Toevoegen<span className="sr-only" data-id="element-8504" data-mp="1j7vj3qp8pvod">: {routine.name}</span> · {euro(totals.price)}
                              </button>}
                          </div>
                        </div>}
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </li>;
    })}
    </ul>;
}